/**
 * Redis Status Card Component
 *
 * Displays Redis server statistics and connection status.
 */

import { Server, HardDrive, Clock, Database } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { RedisStats } from '@/types/cache';
import { formatUptime, getRedisStatusColor } from '@/types/cache';

interface RedisStatusCardProps {
  stats: RedisStats;
}

/**
 * Redis Status Card
 * Shows connection status, memory usage, keys count, and uptime
 */
export function RedisStatusCard({ stats }: RedisStatusCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Redis Server</CardTitle>
        <div className="flex items-center gap-2">
          <div className={`h-2 w-2 rounded-full ${getRedisStatusColor(stats.connected)}`} />
          <Badge variant={stats.connected ? 'default' : 'destructive'}>
            {stats.connected ? 'Connected' : 'Disconnected'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* Memory Used */}
          <div className="flex items-center gap-3">
            <HardDrive className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">{stats.memoryUsed}</p>
              <p className="text-xs text-muted-foreground">Memory Used</p>
            </div>
          </div>

          {/* Peak Memory */}
          <div className="flex items-center gap-3">
            <HardDrive className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">{stats.memoryPeak}</p>
              <p className="text-xs text-muted-foreground">Peak Memory</p>
            </div>
          </div>

          {/* Total Keys */}
          <div className="flex items-center gap-3">
            <Database className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">{stats.totalKeys.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Total Keys</p>
            </div>
          </div>

          {/* Uptime */}
          <div className="flex items-center gap-3">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">{formatUptime(stats.uptime)}</p>
              <p className="text-xs text-muted-foreground">Uptime</p>
            </div>
          </div>

          {/* Version (if available) */}
          {stats.version && (
            <div className="flex items-center gap-3">
              <Server className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">v{stats.version}</p>
                <p className="text-xs text-muted-foreground">Redis Version</p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
